import React, { useState, useEffect } from 'react';
import {
  Users,
  Briefcase,
  UserCheck,
  FileText,
  Calendar,
  Search,
  Plus,
  Filter,
  CheckCircle2,
  XCircle,
  Eye,
  Download,
  Mail,
  Phone,
  MapPin,
  RefreshCw,
  Sparkles,
  Building,
  Check
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dbHelpers } from '../../config/database';

export const HRDashboardView: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'employees' | 'jobs' | 'applications' | 'attendance' | 'pending'>('overview');
  const [employees, setEmployees] = useState<any[]>([]);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [selectedApplication, setSelectedApplication] = useState<any | null>(null);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    email: '',
    password: '',
    role_id: 3,
    department: 'Engineering'
  });

  useEffect(() => {
    loadHRData();
  }, []);

  const loadHRData = async () => {
    setLoading(true);
    try {
      const [empRes, jobsRes, appRes, attRes] = await Promise.all([
        dbHelpers.getEmployees(),
        dbHelpers.getJobs(),
        dbHelpers.getApplications(),
        dbHelpers.getAttendance({ limit: 50 })
      ]);

      if (empRes?.status && Array.isArray(empRes.data)) {
        setEmployees(empRes.data.filter((e: any) => e.approval_status === 'approved'));
        setPendingUsers(empRes.data.filter((e: any) => e.approval_status === 'pending'));
      }
      if (jobsRes?.status && Array.isArray(jobsRes.data)) {
        setJobs(jobsRes.data);
      }
      if (appRes?.status && Array.isArray(appRes.data)) {
        setApplications(appRes.data);
      }
      if (attRes?.status && Array.isArray(attRes.data)) {
        setAttendance(attRes.data);
      }
    } catch (err) {
      console.error('Failed loading HR data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string | number) => {
    try {
      const res = await dbHelpers.approveUser(userId);
      if (res?.status) {
        await loadHRData();
      } else {
        alert(res?.message || 'Approval failed');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectUser = async (userId: string | number) => {
    try {
      const res = await dbHelpers.rejectUser(userId);
      if (res?.status) {
        await loadHRData();
      } else {
        alert(res?.message || 'Rejection failed');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateAppStatus = async (appId: string | number, newStatus: string) => {
    try {
      const res = await dbHelpers.updateApplicationStatus(appId, newStatus);
      if (res?.status) {
        setApplications(prev =>
          prev.map(a => (String(a.id) === String(appId) ? { ...a, status: newStatus } : a))
        );
      } else {
        alert(res?.message || 'Failed to update application');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await dbHelpers.createEmployee({
        name: employeeForm.name,
        email: employeeForm.email,
        password: employeeForm.password,
        role_id: Number(employeeForm.role_id),
        department: employeeForm.department
      });
      if (res?.status) {
        setShowEmployeeModal(false);
        setEmployeeForm({ name: '', email: '', password: '', role_id: 3, department: 'Engineering' });
        await loadHRData();
      } else {
        alert(res?.message || 'Failed to create employee');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const newApplicationsCount = applications.filter(a => (a.status || 'new').toLowerCase() === 'new').length;

  return (
    <div className="flex-1 bg-slate-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Top Header */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                People & Talent Portal
              </span>
              <span className="text-xs text-slate-500 font-medium">HR Operations</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Human Resources Dashboard
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Welcome, <span className="font-semibold text-slate-800">{user?.name || 'HR Manager'}</span>. Manage staff records, recruitment pipelines, applicant resumes, and attendance logs.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadHRData}
              disabled={loading}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 border border-slate-300 shadow-sm cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowEmployeeModal(true)}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </button>
          </div>
        </div>

        {/* HR KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Total Staff</span>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900">{employees.length}</div>
            <p className="text-xs text-blue-600 font-medium mt-1">Active verified employees</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">New Resumes</span>
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-purple-600">{newApplicationsCount}</div>
            <p className="text-xs text-purple-700 font-medium mt-1">{applications.length} total applicants</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Active Jobs</span>
              <Briefcase className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600">{jobs.filter(j => j.is_active).length}</div>
            <p className="text-xs text-emerald-700 font-medium mt-1">Open public positions</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Pending Approvals</span>
              <UserCheck className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-amber-600">{pendingUsers.length}</div>
            <p className="text-xs text-amber-700 font-medium mt-1">User sign-up queue</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-emerald-100 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Today's Check-Ins</span>
              <Calendar className="w-5 h-5 text-emerald-200" />
            </div>
            <div className="text-2xl font-bold">{attendance.length} Logs</div>
            <p className="text-xs text-emerald-100 font-medium mt-1">Daily attendance tracking</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 px-6 pt-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex space-x-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                HR Overview
              </button>

              <button
                onClick={() => setActiveTab('employees')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'employees'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Users className="w-4 h-4" />
                Staff Directory
                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">
                  {employees.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('applications')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'applications'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <FileText className="w-4 h-4" />
                Candidate Applications
                {newApplicationsCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    {newApplicationsCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('jobs')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'jobs'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                Job Openings
                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">
                  {jobs.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('attendance')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'attendance'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Attendance Logs
              </button>

              <button
                onClick={() => setActiveTab('pending')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  activeTab === 'pending'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <UserCheck className="w-4 h-4" />
                Pending Approvals
                {pendingUsers.length > 0 && (
                  <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    {pendingUsers.length}
                  </span>
                )}
              </button>
            </div>

            {/* Search */}
            <div className="pb-4">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter records..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50"
                />
              </div>
            </div>
          </div>

          {/* Sub-Tab: HR Overview */}
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recruitment Pipeline */}
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-slate-900 text-base">Candidate Recruitment Pipeline</h3>
                    <button
                      onClick={() => setActiveTab('applications')}
                      className="text-xs text-emerald-700 font-bold hover:underline"
                    >
                      View Resumes &rarr;
                    </button>
                  </div>

                  <div className="space-y-3">
                    {[
                      { stage: 'New Submissions', count: applications.filter(a => a.status === 'new').length, color: 'bg-blue-500' },
                      { stage: 'Under HR Review', count: applications.filter(a => a.status === 'reviewed').length, color: 'bg-amber-500' },
                      { stage: 'Shortlisted for Interview', count: applications.filter(a => a.status === 'shortlisted').length, color: 'bg-purple-500' },
                      { stage: 'Hired / Placed', count: applications.filter(a => a.status === 'hired').length, color: 'bg-emerald-500' }
                    ].map(stg => (
                      <div key={stg.stage} className="space-y-1">
                        <div className="flex justify-between text-xs font-semibold text-slate-700">
                          <span>{stg.stage}</span>
                          <span>{stg.count} Candidates</span>
                        </div>
                        <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${stg.color}`}
                            style={{
                              width: `${applications.length > 0 ? (stg.count / applications.length) * 100 : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Department Staff Breakdown */}
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-slate-900 text-base">Staff by Department</h3>
                    <button
                      onClick={() => setActiveTab('employees')}
                      className="text-xs text-emerald-700 font-bold hover:underline"
                    >
                      Manage Staff &rarr;
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { dept: 'Engineering', count: employees.filter(e => (e.department || '').toLowerCase() === 'engineering').length },
                      { dept: 'Sales', count: employees.filter(e => (e.department || '').toLowerCase() === 'sales').length },
                      { dept: 'Marketing', count: employees.filter(e => (e.department || '').toLowerCase() === 'marketing').length },
                      { dept: 'HR & People', count: employees.filter(e => (e.department || '').toLowerCase().includes('hr') || Number(e.role_id) === 2).length }
                    ].map(d => (
                      <div key={d.dept} className="bg-white p-3.5 rounded-xl border border-slate-200">
                        <div className="text-xs text-slate-500 font-medium">{d.dept}</div>
                        <div className="text-lg font-bold text-slate-900 mt-1">{d.count} Members</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub-Tab: Staff Directory */}
          {activeTab === 'employees' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Employee</th>
                      <th className="py-3 px-4">Role</th>
                      <th className="py-3 px-4">Department</th>
                      <th className="py-3 px-4">Joined Date</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {employees
                      .filter(emp =>
                        (emp.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (emp.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (emp.department || '').toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map(emp => (
                        <tr key={emp.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-slate-900">{emp.name}</div>
                            <div className="text-xs text-slate-500">{emp.email}</div>
                          </td>
                          <td className="py-3.5 px-4 capitalize font-medium text-slate-700">
                            {Number(emp.role_id) === 1
                              ? 'Admin'
                              : Number(emp.role_id) === 2
                              ? 'HR'
                              : Number(emp.role_id) === 5
                              ? 'Finance'
                              : Number(emp.role_id) === 8 || (emp.department || '').toLowerCase() === 'sales'
                              ? 'Sales'
                              : Number(emp.role_id) === 9 || (emp.department || '').toLowerCase() === 'marketing'
                              ? 'Marketing'
                              : 'Employee'}
                          </td>
                          <td className="py-3.5 px-4 text-xs font-semibold text-slate-800">
                            {emp.department || 'General'}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-500">
                            {emp.created_at ? new Date(emp.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                              Active
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab: Candidate Applications */}
          {activeTab === 'applications' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Candidate</th>
                      <th className="py-3 px-4">Position Applied</th>
                      <th className="py-3 px-4">Location</th>
                      <th className="py-3 px-4">Experience</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {applications.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-400">
                          No candidate applications submitted yet.
                        </td>
                      </tr>
                    ) : (
                      applications
                        .filter(a =>
                          (a.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (a.position_applied || a.job_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (a.email || '').toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map(app => (
                          <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3.5 px-4">
                              <div className="font-bold text-slate-900">{app.full_name}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                                <Mail className="w-3 h-3 text-slate-400" />
                                <span>{app.email}</span>
                                {app.phone && <span>• {app.phone}</span>}
                              </div>
                            </td>
                            <td className="py-3.5 px-4">
                              <span className="font-semibold text-slate-800">
                                {app.position_applied || app.job_title || 'General Applicant'}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-xs text-slate-600">
                              {app.location || 'India'}
                            </td>
                            <td className="py-3.5 px-4 text-xs font-semibold text-slate-800">
                              {app.experience_years ? `${app.experience_years} yrs` : 'Fresh'}
                            </td>
                            <td className="py-3.5 px-4">
                              <select
                                value={(app.status || 'new').toLowerCase()}
                                onChange={e => handleUpdateAppStatus(app.id, e.target.value)}
                                className={`text-xs px-2.5 py-1 rounded-full font-bold border cursor-pointer ${
                                  app.status === 'hired'
                                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                    : app.status === 'shortlisted'
                                    ? 'bg-purple-100 text-purple-800 border-purple-300'
                                    : app.status === 'reviewed'
                                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                                    : app.status === 'rejected'
                                    ? 'bg-red-100 text-red-800 border-red-300'
                                    : 'bg-amber-100 text-amber-800 border-amber-300'
                                }`}
                              >
                                <option value="new">New</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="shortlisted">Shortlisted</option>
                                <option value="hired">Hired</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </td>
                            <td className="py-3.5 px-4 text-right space-x-2">
                              {app.resume_url && (
                                <a
                                  href={app.resume_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  Resume
                                </a>
                              )}
                              <button
                                onClick={() => {
                                  setSelectedApplication(app);
                                  setShowAppModal(true);
                                }}
                                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1 cursor-pointer"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab: Job Openings */}
          {activeTab === 'jobs' && (
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-slate-900 text-base">Company Job Openings</h3>
                <a
                  href="#jobs"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs"
                >
                  Manage Full Job Postings
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {jobs.map(j => (
                  <div key={j.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-sm text-slate-900">{j.title}</h4>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          j.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {j.is_active ? 'Active' : 'Closed'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{j.department} • {j.location}</p>
                    <div className="text-xs text-slate-600 font-medium">Exp: {j.experience || j.experience_required}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sub-Tab: Attendance Logs */}
          {activeTab === 'attendance' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Employee</th>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Check-In</th>
                      <th className="py-3 px-4">Check-Out</th>
                      <th className="py-3 px-4">Working Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {attendance.slice(0, 20).map((att, idx) => (
                      <tr key={att.id || idx} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-bold text-slate-900">{att.name || `Staff #${att.user_id}`}</td>
                        <td className="py-3 px-4 text-xs text-slate-600">{att.date}</td>
                        <td className="py-3 px-4 text-xs font-mono text-emerald-700">
                          {att.check_in ? new Date(att.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono text-slate-500">
                          {att.check_out ? new Date(att.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 capitalize">
                            {att.working_status || 'present'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab: Pending Approvals */}
          {activeTab === 'pending' && (
            <div className="p-6">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No pending user registration requests.
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingUsers.map(pUser => (
                    <div
                      key={pUser.id}
                      className="bg-white p-4 rounded-xl border border-amber-200 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div>
                        <h4 className="font-bold text-slate-900 text-base">{pUser.name}</h4>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <span>{pUser.email}</span>
                          <span>• Dept: {pUser.department || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApproveUser(pUser.id)}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1 shadow-sm cursor-pointer"
                        >
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handleRejectUser(pUser.id)}
                          className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold rounded-xl transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Application Detail Modal */}
      {showAppModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 border border-slate-200">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Candidate Application #{selectedApplication.id}</span>
                <h3 className="text-lg font-bold text-slate-900">{selectedApplication.full_name}</h3>
                <p className="text-xs text-slate-500">Applied for: {selectedApplication.position_applied || selectedApplication.job_title || 'General'}</p>
              </div>
              <button
                onClick={() => setShowAppModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-1.5 text-slate-700">
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Email:</span>
                  <span className="font-medium text-slate-900">{selectedApplication.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Phone:</span>
                  <span className="font-medium text-slate-900">{selectedApplication.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Location:</span>
                  <span className="font-medium text-slate-900">{selectedApplication.location || 'India'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Experience:</span>
                  <span className="font-medium text-slate-900">{selectedApplication.experience_years ? `${selectedApplication.experience_years} Years` : 'Fresh'}</span>
                </div>
                {selectedApplication.expected_salary && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-500">Expected Salary:</span>
                    <span className="font-medium text-slate-900">₹{Number(selectedApplication.expected_salary).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>

              {selectedApplication.cover_letter && (
                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Cover Letter / Candidate Notes:</label>
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {selectedApplication.cover_letter}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
              {selectedApplication.resume_url && (
                <a
                  href={selectedApplication.resume_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download Resume
                </a>
              )}
              <button
                onClick={() => setShowAppModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-900">Add New Team Member</h3>
              <button onClick={() => setShowEmployeeModal(false)} className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer">✕</button>
            </div>

            <form onSubmit={handleAddEmployee} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Priya Sharma"
                  value={employeeForm.name}
                  onChange={e => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="e.g. priya@x2infotech.in"
                  value={employeeForm.email}
                  onChange={e => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Password</label>
                <input
                  type="password"
                  required
                  placeholder="Minimum 6 characters"
                  value={employeeForm.password}
                  onChange={e => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Department</label>
                <select
                  value={employeeForm.department}
                  onChange={e => setEmployeeForm({ ...employeeForm, department: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white cursor-pointer"
                >
                  <option value="Engineering">Engineering</option>
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Human Resources">Human Resources</option>
                  <option value="Design">Design</option>
                  <option value="Management">Management</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Role</label>
                <select
                  value={employeeForm.role_id}
                  onChange={e => setEmployeeForm({ ...employeeForm, role_id: Number(e.target.value) })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white cursor-pointer"
                >
                  <option value={3}>Employee</option>
                  <option value={2}>HR</option>
                  <option value={8}>Sales Team</option>
                  <option value={9}>Marketing Team</option>
                </select>
              </div>

              <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEmployeeModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                >
                  Create Staff Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default HRDashboardView;
